(() => {
  if (customElements.get('nekoan-smooth-rail')) return;
  class NekoanSmoothRail extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.rail = this.querySelector('[data-drag-parent], [data-nekoan-lifestyle-feed]');
      if (!this.rail) return;
      this.controller = new AbortController();
      const signal = this.controller.signal;
      const lang = document.documentElement.lang.split('-')[0];
      const labels = {en: 'Scroll images', de: 'Bilder scrollen', es: 'Desplazar imágenes'};
      this.bar = document.createElement('input');
      this.bar.type = 'range';
      this.bar.className = 'nekoan-rail-bar';
      this.bar.min = '0';
      this.bar.max = '1000';
      this.bar.step = '1';
      this.bar.value = '0';
      this.bar.setAttribute('aria-label', labels[lang] || labels.en);
      this.append(this.bar);
      this.rail.classList.add('nekoan-native-rail');
      this.rail.style.scrollBehavior = 'auto';
      this.rail.style.scrollSnapType = 'none';
      this.rail.addEventListener('scroll', () => {
        if (!this.syncFrame) this.syncFrame = requestAnimationFrame(() => {
          this.syncFrame = 0;
          this.sync();
        });
      }, {passive: true, signal});
      this.bar.addEventListener('input', () => {
        this.stop();
        this.rail.scrollLeft = Number(this.bar.value) / 1000 * this.maxScroll();
      }, {signal});
      this.rail.addEventListener('pointerdown', event => {
        this.stop();
        if (event.pointerType !== 'mouse' || event.button !== 0 || event.target.closest('button,input,select,textarea')) return;
        this.drag = {id: event.pointerId, x: event.clientX, left: this.rail.scrollLeft, lastX: event.clientX, time: performance.now(), velocity: 0, moved: false};
      }, {signal});
      this.rail.addEventListener('pointermove', event => {
        const drag = this.drag;
        if (!drag || drag.id !== event.pointerId) return;
        const distance = event.clientX - drag.x;
        if (!drag.moved && Math.abs(distance) < 5) return;
        if (!drag.moved) {
          drag.moved = true;
          this.rail.setPointerCapture(event.pointerId);
          this.rail.classList.add('nekoan-dragging');
        }
        event.preventDefault();
        const now = performance.now();
        drag.velocity = Math.max(-2.5, Math.min(2.5, (drag.lastX - event.clientX) / Math.max(1, now - drag.time)));
        drag.lastX = event.clientX;
        drag.time = now;
        this.rail.scrollLeft = drag.left - distance;
      }, {signal});
      const release = event => {
        const drag = this.drag;
        if (!drag || drag.id !== event.pointerId) return;
        this.drag = null;
        this.rail.classList.remove('nekoan-dragging');
        if (this.rail.hasPointerCapture(event.pointerId)) this.rail.releasePointerCapture(event.pointerId);
        if (!drag.moved) return;
        this.suppressClickUntil = performance.now() + 250;
        if (event.type === 'pointerup' && performance.now() - drag.time < 80 && !matchMedia('(prefers-reduced-motion: reduce)').matches) this.coast(drag.velocity);
      };
      window.addEventListener('pointerup', release, {signal});
      this.rail.addEventListener('pointercancel', release, {signal});
      this.rail.addEventListener('pointerleave', event => {
        if (this.drag && !this.drag.moved) this.drag = null;
      }, {signal});
      this.rail.addEventListener('dragstart', event => event.preventDefault(), {signal});
      this.rail.addEventListener('click', event => {
        if (performance.now() < (this.suppressClickUntil || 0)) {
          event.preventDefault();
          event.stopImmediatePropagation();
        }
      }, {capture: true, signal});
      this.rail.addEventListener('wheel', () => this.stop(), {passive: true, signal});
      this.observer = new ResizeObserver(() => this.sync());
      this.observer.observe(this.rail);
      for (const child of this.rail.children) this.observer.observe(child);
      this.sync();
    }
    maxScroll() {
      return Math.max(0, this.rail.scrollWidth - this.rail.clientWidth);
    }
    sync() {
      const max = this.maxScroll();
      const disabled = max < 2;
      const value = String(max ? Math.round(this.rail.scrollLeft / max * 1000) : 0);
      const thumbWidth = Math.max(44, this.bar.clientWidth * this.rail.clientWidth / Math.max(1, this.rail.scrollWidth)) + 'px';
      if (this.bar.disabled !== disabled) this.bar.disabled = disabled;
      if (this.bar.value !== value) this.bar.value = value;
      if (this.thumbWidth !== thumbWidth) {
        this.thumbWidth = thumbWidth;
        this.bar.style.setProperty('--nekoan-thumb-width', thumbWidth);
      }
    }
    stop() {
      cancelAnimationFrame(this.momentumFrame);
      this.momentumFrame = 0;
    }
    coast(velocity) {
      let last = performance.now();
      const tick = now => {
        const elapsed = Math.min(32, now - last);
        last = now;
        velocity *= Math.pow(0.9, elapsed / 16.67);
        const before = this.rail.scrollLeft;
        this.rail.scrollLeft += velocity * elapsed;
        if (Math.abs(velocity) > 0.02 && Math.abs(this.rail.scrollLeft - before) > 0.1) this.momentumFrame = requestAnimationFrame(tick);
      };
      this.momentumFrame = requestAnimationFrame(tick);
    }
    disconnectedCallback() {
      this.stop();
      cancelAnimationFrame(this.syncFrame);
      this.syncFrame = 0;
      this.thumbWidth = null;
      this.observer?.disconnect();
      this.controller?.abort();
      this.controller = null;
      this.drag = null;
      this.bar?.remove();
    }
  }
  customElements.define('nekoan-smooth-rail', NekoanSmoothRail);
})();

